import { ReactNode } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { CallSplit, Inventory2, SwapHoriz } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { ConversionKind } from "../../util/conversionMath";

const TYPES: {
	value: ConversionKind;
	icon: ReactNode;
	titleKey: string;
	helpKey: string;
	exampleKey: string;
}[] = [
	{
		value: "KIT",
		icon: <Inventory2 fontSize="small" />,
		titleKey: "conversion.types.kitTitle",
		helpKey: "conversion.types.kitHelp",
		exampleKey: "conversion.types.kitExample",
	},
	{
		value: "DE-KIT",
		icon: <CallSplit fontSize="small" />,
		titleKey: "conversion.types.dekitTitle",
		helpKey: "conversion.types.dekitHelp",
		exampleKey: "conversion.types.dekitExample",
	},
	{
		value: "SWAP",
		icon: <SwapHoriz fontSize="small" />,
		titleKey: "conversion.types.swapTitle",
		helpKey: "conversion.types.swapHelp",
		exampleKey: "conversion.types.swapExample",
	},
];

interface Props {
	value: ConversionKind | "";
	onChange: (value: ConversionKind) => void;
	disabled?: boolean;
}

const ConversionTypePicker = ({ value, onChange, disabled }: Props) => {
	const { t } = useTranslation();

	return (
		<Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.5 }}>
			{TYPES.map((option) => {
				const selected = value === option.value;
				return (
					<Paper
						key={option.value}
						component="button"
						type="button"
						disabled={disabled}
						onClick={() => onChange(option.value)}
						elevation={selected ? 3 : 0}
						sx={{
							flex: 1,
							font: "inherit",
							color: "inherit",
							textAlign: "left",
							cursor: disabled ? "default" : "pointer",
							p: 1.25,
							border: "1px solid",
							borderColor: selected ? "primary.main" : "divider",
							bgcolor: selected ? "action.selected" : "background.paper",
							opacity: disabled ? 0.65 : 1,
						}}
					>
						<Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
							<Box sx={{ color: selected ? "primary.main" : "text.secondary", display: "flex" }}>
								{option.icon}
							</Box>
							<Typography variant="subtitle2">{t(`common:${option.titleKey}`)}</Typography>
						</Stack>
						<Typography variant="body2" color="text.secondary">
							{t(`common:${option.helpKey}`)}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{t(`common:${option.exampleKey}`)}
						</Typography>
					</Paper>
				);
			})}
		</Stack>
	);
};

export default ConversionTypePicker;
